<template>
    <div class="block wp-10 copy-element">
        <el-tooltip
            :content="tooltipMsg"
            placement="top"
            :disabled="hasPermission || data.status === 'ok'"
        >
            <a :href="hasPermission ? urlFormatted() : null" target="_blank" class="gray-100 block">
                <div v-if="isBlockedOrDeleted(data)" class="flex-level">
                    <div
                        v-if="displayIconBlockedOrDeleted"
                        :class="['avatar mr-1', errorsData[data.status].class]"
                    >
                        <i
                            :class="errorsData[data.status].icon"
                            style="line-height: initial; width: 32px"
                            class="block"
                        ></i>
                    </div>
                    <div class="strike ellipsis avatar-name">{{ fullname }}</div>
                </div>
                <div v-else class="flex-level">
                    <avatar-randomized
                        v-if="displayAvatar"
                        :firstname="firstname"
                        :lastname="lastname"
                        :avatarImage="
                            data.avatar && data.avatar.type !== 'initials'
                                ? data.avatar.value
                                : null
                        "
                        class="mr-1"
                        :size="size"
                    />
                    <div
                        :class="[
                            statusClass(data),
                            hasHoveredActions ? 'avatar-name-hovered' : '',
                            wrapText ? 'ellipsis avatar-name' : '',
                        ]"
                    >
                        {{ fullname }}
                    </div>
                    <slot name="label"> </slot>
                </div>
            </a>
        </el-tooltip>

        <hovered-actions
            v-if="hasHoveredActions"
            :actions="actions"
            @email-clicked="emailClicked"
            @phone-clicked="phoneClicked"
        />
    </div>
</template>
<script>
import AvatarMixins from '../../mixins/AvatarMixins';
import EmailMixins from '../../mixins/EmailMixins';
import HoveredActions from './HoveredActions.vue';
import AvatarRandomized from 'vcomponents/utilities/AvatarRandomized.vue';

export default {
    components: {
        'hovered-actions': HoveredActions,
        AvatarRandomized,
    },
    mixins: [AvatarMixins, EmailMixins],
    props: {
        data: {
            type: Object,
            required: true,
        },
        size: {
            type: String,
            required: false,
            default: 'small',
        },
        context: {
            type: String,
            required: false,
            default: '',
        },
        relatedData: {
            type: [Object, Boolean],
            required: false,
            default: false,
        },
        enableAvatar: {
            type: Boolean,
            required: false,
            default: true,
        },
        wrapText: {
            type: Boolean,
            required: false,
            default: true,
        },
    },
    data() {
        return {
            errorsData: {
                blocked: {
                    msg: '',
                    class: 'avatar-blocked',
                    icon: 'icon-lock',
                },
                deleted: {
                    msg: '',
                    class: 'avatar-deleted',
                    icon: 'icon-close',
                },
            },
            linkedtype: this.data.type,
            linkedid: this.data.id,
        };
    },
    computed: {
        lastname() {
            return this.data.lastname ?? this.data.name ?? this.data.fullName;
        },
        firstname() {
            return this.data.firstname ?? this.data.forename ?? this.data.fullName;
        },
        hasPicture() {
            if (this.data.avatar && this.enableAvatar) {
                return (
                    this.data.avatar.type === 'twitteruserpic' ||
                    this.data.avatar.type === 'picture'
                );
            }
            return false;
        },

        hasPermission() {
            if (this.isBlockedOrDeleted(this.data)) {
                return false;
            }
            if (this.data.hasOwnProperty('key')) {
                return this.data.permission === true;
            } else {
                return true;
            }
        },

        fullname() {
            if (this.data.fullName && this.data.fullName !== '') {
                return this.data.fullName;
            }
            if (this.data.full_name && this.data.full_name !== '') {
                return this.data.full_name;
            }
            if (
                !_.isNil(this.data.lastname) &&
                !!this.data.lastname.length &&
                !_.isNil(this.data.firstname) &&
                !!this.data.firstname.length
            ) {
                return `${this.data.firstname} ${this.data.lastname}`;
            }
            if (this.data.name && this.data.name != '') {
                return this.data.name;
            }
            return this.$l10n.__('Utilisateur non trouvé.');
        },

        clipboardEmailEnabled() {
            if (
                this.data.type !== 'staff' &&
                this.data.type !== 'owner' &&
                this.data.email &&
                this.data.email !== '' &&
                !this.isBlockedOrDeleted(this.data)
            ) {
                return true;
            }
            return false;
        },
        clipboardPhoneEnabled() {
            if (
                this.data.type !== 'staff' &&
                this.data.type !== 'owner' &&
                this.phoneNumber !== null &&
                !this.isBlockedOrDeleted(this.data)
            ) {
                return true;
            }
            return false;
        },
        tooltipMsg() {
            return this.errorsData[this.data.status] && this.errorsData[this.data.status].msg
                ? this.errorsData[this.data.status].msg
                : '';
        },
        hasHoveredActions() {
            return this.clipboardEmailEnabled || this.clipboardPhoneEnabled;
        },
        actions() {
            const actions = [];
            if (this.clipboardEmailEnabled) {
                actions.push({
                    id: 'email',
                    data: this.data.email,
                    textTooltip: this.data.email,
                    useNotif: false,
                    icon: 'email',
                });
            }
            if (this.clipboardPhoneEnabled) {
                actions.push({
                    id: 'phone',
                    data: this.data.mobile || this.data.tel,
                    textTooltip: this.data.mobile || this.data.tel,
                    useNotif: false,
                    icon: 'phone',
                });
            }
            return actions;
        },
        phoneNumber() {
            if (this.data.mobile && this.data.mobile !== '') {
                return this.data.mobile;
            } else if (this.data.tel && this.data.tel !== '') {
                return this.data.tel;
            }
            return null;
        },
        displayAvatar() {
            switch (this.data.type) {
                case 'staff':
                case 'owner':
                    return true;
                default:
                    return false;
            }
        },
        displayIconBlockedOrDeleted() {
            switch (this.data.type) {
                case 'staff':
                case 'owner':
                    return true;
                default:
                    return false;
            }
        },
    },
    created() {
        this.setErrorMsgByType();
    },
    methods: {
        urlFormatted() {
            let url = '';
            if (!this.isBlockedOrDeleted(this.data)) {
                if (this.data.url) {
                    url = this.data.url;
                } else if (this.data.link) {
                    url = this.data.link;
                } else {
                    switch (this.data.type) {
                        case 'people':
                            this.linkedid = this.data.peopleid;
                            url = '/?_f=people&id=' + this.data.peopleid;
                            break;
                        case 'third':
                            this.linkedid = this.data.thirdid;
                            url =
                                '/?_f=third&thirdid=' +
                                this.data.thirdid +
                                '&thirdtype=' +
                                this.data.relationType;
                            break;
                        case 'staff':
                            this.linkedid = this.data.staffid;
                            url = '/staff/' + this.data.staffid;
                            break;
                    }
                }
            }
            return url.length > 0 ? url : false;
        },
        emailClicked() {
            this.checkEmailProvider({}, { checkPrefill: true });
        },
        phoneClicked() {
            window.open(`tel:${this.phoneNumber}`, '_self');
        },
        setErrorMsgByType() {
            switch (this.data.type) {
                case 'people':
                    this.errorsData.blocked.msg = this.$l10n.__('Contact bloqué');
                    this.errorsData.deleted.msg = this.$l10n.__('Contact supprimé');
                    break;
                case 'third':
                    this.errorsData.blocked.msg = this.$l10n.__(
                        'Client/Prospect/Fournisseur bloqué',
                    );
                    this.errorsData.deleted.msg = this.$l10n.__(
                        'Client/Prospect/Fournisseur supprimé',
                    );
                    break;
                case 'owner':
                case 'staff':
                    this.errorsData.blocked.msg = this.$l10n.__('Collaborateur bloqué');
                    this.errorsData.deleted.msg = this.$l10n.__('Collaborateur supprimé');
                    break;
            }
            return;
        },
    },
};
</script>

<style scoped lang="scss">
@import '~@sellsy/styleguide/src/_variables';
.avatar-name {
    transition: all 0.3s linear;
    vertical-align: middle;

    &:not(.avatar-name-disabled):hover {
        color: $primary-50;
    }
    width: auto;
}

.avatar-tiny {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 16px;
    width: 16px;
    padding: 0;
    border-radius: 2px;
    background-color: $dark-blue;
    color: $white !important;
}

.opportunityList .avatar-name {
    max-width: 200px;
}

.block {
    display: block;
}
</style>
